/**
 * The little connection pill in the bottom-left corner.
 *
 * Two lines of information, both of which answer a question you actually ask
 * while working: is the socket live, and did the last command land?
 */

const LABELS = {
  connecting: 'bridge connecting…',
  connected: 'bridge connected',
  disconnected: 'bridge offline',
};

export function createStatusIndicator(root = document.getElementById('status')) {
  const dot = root.querySelector('#status-dot');
  const text = root.querySelector('#status-text');
  const detail = root.querySelector('#status-detail');
  const selection = document.getElementById('status-selection');
  const mode = document.getElementById('status-mode');
  const tool = document.getElementById('status-tool');
  const space = document.getElementById('status-space');
  let lastCommand = null;
  root.setAttribute('role', 'status');
  root.setAttribute('aria-label', 'Bridge connection');

  function paint(state, note) {
    root.dataset.state = state;
    text.textContent = LABELS[state] ?? state;
    // Once a command has run, the last command is the more useful thing to show;
    // fall back to the connection note (URL, retry countdown) before that.
    detail.textContent = lastCommand ?? note ?? '—';
    void dot; // styling is driven by [data-state] on the container
  }

  function stamp(message) {
    const time = new Date().toLocaleTimeString([], { hour12: false });
    lastCommand = `${message} · ${time}`;
    detail.textContent = lastCommand;
  }

  return {
    setState(state, note) {
      paint(state, note);
    },
    setLastCommand(cmd, ok) {
      stamp(`${ok ? '✓' : '✗'} ${cmd}`);
    },
    /** Shown after the scene is rebuilt from the bridge's log. */
    setRestored(count) {
      if (count > 0) stamp(`↻ restored ${count} object${count === 1 ? '' : 's'}`);
    },
    /**
     * WebGL context loss is worth surfacing on mobile: the canvas goes blank
     * while everything else keeps working, which otherwise looks inexplicable.
     */
    setContext(state) {
      stamp(state === 'lost' ? '⚠ WebGL context lost' : '✓ WebGL context restored');
    },
    setWorkspace({ selectedCount = 0, activeTool = 'select', editorMode = 'Object', transformSpace = 'world' } = {}) {
      const count = Number(selectedCount) || 0;
      const toolName = String(activeTool).replace('-', ' ');
      const spaceName = String(transformSpace);
      if (mode) mode.textContent = editorMode;
      selection.textContent = `${count} selected`;
      tool.textContent = `${toolName[0].toUpperCase()}${toolName.slice(1)}`;
      space.textContent = `${spaceName[0].toUpperCase()}${spaceName.slice(1)}`;
    },
    setBuild(label) {
      if (label) root.title = `build ${label}`;
    },
  };
}
